<template>
  <div class="legal-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Banner -->
    <div class="legal-hero seller-terms-hero">
      <div class="hero-content">
        <h1>IPA Seller Terms & Conditions</h1>
        <p>Agreement for Marketplace Sellers</p>
        <div class="last-updated">
          <i class="fa-solid fa-calendar"></i>
          Last Updated: April 27, 2026
        </div>
      </div>
      <div class="hero-actions">
        <router-link to="/ipromiseafrica/resale/seller-sign-up" class="hero-btn primary">
          <i class="fa-solid fa-user-plus"></i>
          Become a Seller
        </router-link>
        <router-link to="/ipromiseafrica/legal/buyer-terms" class="hero-btn secondary">
          <i class="fa-solid fa-file-contract"></i>
          View Buyer Terms
        </router-link>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Important Notice -->
    <div class="important-notice">
      <div class="notice-header">
        <i class="fa-solid fa-file-circle-exclamation"></i>
        <h3>Important Legal Agreement</h3>
      </div>
      <div class="notice-content">
        <p>
          By registering as a Seller on the IPromiseAfrica Marketplace ("IPA"), you agree to the following 
          Terms & Conditions. <strong>Please read them carefully before proceeding.</strong>
        </p>
        <div class="notice-highlights">
          <div class="highlight">
            <i class="fa-solid fa-circle-check"></i>
            <span>This is a legally binding agreement</span>
          </div>
          <div class="highlight">
            <i class="fa-solid fa-scale-balanced"></i>
            <span>You accept full liability for your products</span>
          </div>
          <div class="highlight">
            <i class="fa-solid fa-shield"></i>
            <span>Violations may result in account termination</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="legal-container">
      <div class="legal-content seller-terms-content">
        <!-- Terms Grid -->
        <div class="terms-grid">
          <!-- Eligibility -->
          <div class="term-card" data-aos="fade-up">
            <div class="term-number">1</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-id-card"></i>
                Eligibility
              </h3>
              <span class="term-subtitle">Who can be a Seller</span>
            </div>
            <div class="term-body">
              <p>To register as a Seller on IPA, you confirm that:</p>
              <ul class="term-list">
                <li>
                  <i class="fa-solid fa-user-check"></i>
                  <span>You are at least <strong>18 years old</strong> and legally capable of entering into binding contracts</span>
                </li>
                <li>
                  <i class="fa-solid fa-circle-info"></i>
                  <span>All information you provide during registration is <strong>accurate, up-to-date, and complete</strong></span>
                </li>
                <li>
                  <i class="fa-solid fa-scale-balanced"></i>
                  <span>You will operate in compliance with <strong>applicable local and international laws</strong></span>
                </li>
              </ul>
              <div class="term-warning">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <p>Providing false information may result in immediate account termination</p>
              </div>
            </div>
          </div>

          <!-- Seller Account Responsibilities -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="100">
            <div class="term-number">2</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-user-shield"></i>
                Seller Account Responsibilities
              </h3>
              <span class="term-subtitle">Managing your seller account</span>
            </div>
            <div class="term-body">
              <p>You agree to:</p>
              <div class="responsibility-grid">
                <div class="responsibility">
                  <i class="fa-solid fa-lock"></i>
                  <span>Maintain the <strong>security</strong> of your account credentials</span>
                </div>
                <div class="responsibility">
                  <i class="fa-solid fa-sync"></i>
                  <span>Update your business profile, contact details, and payment information when needed</span>
                </div>
                <div class="responsibility">
                  <i class="fa-solid fa-user-tag"></i>
                  <span>Take <strong>full responsibility</strong> for all activity conducted under your account</span>
                </div>
                <div class="responsibility">
                  <i class="fa-solid fa-user-xmark"></i>
                  <span>Not create multiple Seller accounts unless authorized by IPA</span>
                </div>
              </div>
              <div class="term-note">
                <i class="fa-solid fa-ban"></i>
                <p>IPA may suspend or terminate accounts suspected of fraudulent, harmful, or abusive activity</p>
              </div>
            </div>
          </div>

          <!-- Product Listings & Accuracy -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="200">
            <div class="term-number">3</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-clipboard-check"></i>
                Product Listings & Accuracy
              </h3>
              <span class="term-subtitle">Creating honest product listings</span>
            </div>
            <div class="term-body">
              <div class="listing-rules">
                <div class="rule">
                  <i class="fa-solid fa-check-circle"></i>
                  <div>
                    <strong>Truthful Information:</strong> All product information must be truthful, complete, and not misleading
                  </div>
                </div>
                <div class="rule">
                  <i class="fa-solid fa-images"></i>
                  <div>
                    <strong>Accurate Images:</strong> Images uploaded must accurately represent the physical product
                  </div>
                </div>
                <div class="rule">
                  <i class="fa-solid fa-ban"></i>
                  <div>
                    <strong>No Illegal Items:</strong> You will not list illegal, counterfeit, or restricted items
                  </div>
                </div>
                <div class="rule">
                  <i class="fa-solid fa-box-open"></i>
                  <div>
                    <strong>Product Availability:</strong> Your product must be available and deliverable at the time of listing
                  </div>
                </div>
              </div>
              <div class="term-warning">
                <i class="fa-solid fa-trash"></i>
                <p>IPA reserves the right to remove or reject any listing that violates policy</p>
              </div>
            </div>
          </div>

          <!-- Pricing & Negotiation -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="300">
            <div class="term-number">4</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-tag"></i>
                Pricing & Negotiation
              </h3>
              <span class="term-subtitle">Setting and negotiating prices</span>
            </div>
            <div class="term-body">
              <div class="price-section">
                <div class="price-rule">
                  <i class="fa-solid fa-money-bill-wave"></i>
                  <div>
                    <p><strong>You are solely responsible</strong> for setting the price of your products</p>
                  </div>
                </div>
                <div class="negotiation-section">
                  <p><strong>If you enable "Negotiable":</strong></p>
                  <ul class="term-list">
                    <li>
                      <i class="fa-solid fa-comment-dots"></i>
                      <span>Respond honestly to offers</span>
                    </li>
                    <li>
                      <i class="fa-solid fa-handshake"></i>
                      <span>Honor agreed pricing</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="term-note">
                <i class="fa-solid fa-scale-unbalanced"></i>
                <p>IPA is <strong>not responsible</strong> for pricing disputes</p>
              </div>
            </div>
          </div>

          <!-- Order Fulfillment & Delivery -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="400">
            <div class="term-number">5</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-truck-fast"></i>
                Order Fulfillment & Delivery
              </h3>
              <span class="term-subtitle">Delivering on your promises</span>
            </div>
            <div class="term-body">
              <div class="delivery-rules">
                <div class="delivery-rule">
                  <i class="fa-solid fa-comments"></i>
                  <div>
                    <p><strong>Communication:</strong> Communicate clearly with buyers</p>
                  </div>
                </div>
                <div class="delivery-rule">
                  <i class="fa-solid fa-clock"></i>
                  <div>
                    <p><strong>Timely Delivery:</strong> Deliver items on time as agreed with the buyer</p>
                  </div>
                </div>
                <div class="delivery-rule">
                  <i class="fa-solid fa-location-dot"></i>
                  <div>
                    <p><strong>Safe Locations:</strong> Meet buyers at safe, public locations if delivery requires physical exchange</p>
                  </div>
                </div>
                <div class="delivery-rule">
                  <i class="fa-solid fa-clipboard-list"></i>
                  <div>
                    <p><strong>Product Accuracy:</strong> Ensure the product matches the description and condition stated</p>
                  </div>
                </div>
              </div>
              <div class="term-warning">
                <i class="fa-solid fa-user-slash"></i>
                <p>Failure to deliver or fraudulent behavior may result in account suspension</p>
              </div>
            </div>
          </div>

          <!-- Payments -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="500">
            <div class="term-number">6</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-credit-card"></i>
                Payments
              </h3>
              <span class="term-subtitle">Financial transactions</span>
            </div>
            <div class="term-body">
              <div class="payment-info">
                <div class="payment-rule">
                  <i class="fa-solid fa-money-check-dollar"></i>
                  <div>
                    <p>Payments between Seller and Buyer are <strong>direct</strong> unless IPA provides a payment escrow service</p>
                  </div>
                </div>
                <div class="payment-rule">
                  <i class="fa-solid fa-eye"></i>
                  <div>
                    <p>Sellers are responsible for <strong>verifying receipt of funds</strong> before handing over an item</p>
                  </div>
                </div>
              </div>
              <div class="term-warning important">
                <i class="fa-solid fa-circle-exclamation"></i>
                <p>IPA is <strong>not responsible</strong> for financial disputes unless a verified IPA payment system is used</p>
              </div>
            </div>
          </div>

          <!-- Prohibited Activities -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="600">
            <div class="term-number">7</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-ban"></i>
                Prohibited Activities
              </h3>
              <span class="term-subtitle">Activities that will get you banned</span>
            </div>
            <div class="term-body">
              <p>Sellers must <strong>not</strong>:</p>
              <div class="prohibited-list">
                <div class="prohibited-item">
                  <i class="fa-solid fa-user-secret"></i>
                  <span>Engage in scams, price manipulation, counterfeit sales, or fraudulent behavior</span>
                </div>
                <div class="prohibited-item">
                  <i class="fa-solid fa-robot"></i>
                  <span>Use fake accounts, fake reviews, or artificially boost ratings</span>
                </div>
                <div class="prohibited-item">
                  <i class="fa-solid fa-comment-slash"></i>
                  <span>Harass customers or engage in abusive communication</span>
                </div>
                <div class="prohibited-item">
                  <i class="fa-solid fa-door-open"></i>
                  <span>Circumvent IPA for off-platform sales when leads originate from IPA</span>
                </div>
              </div>
              <div class="term-warning critical">
                <i class="fa-solid fa-gavel"></i>
                <p>Violations may lead to termination and reporting to authorities</p>
              </div>
            </div>
          </div>

          <!-- Ratings & Reviews -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="700">
            <div class="term-number">8</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-star"></i>
                Ratings & Reviews
              </h3>
              <span class="term-subtitle">Customer feedback system</span>
            </div>
            <div class="term-body">
              <div class="ratings-info">
                <div class="rating-rule">
                  <i class="fa-solid fa-comment-medical"></i>
                  <div>
                    <p>Customers may rate and review Sellers</p>
                  </div>
                </div>
                <div class="rating-rule">
                  <i class="fa-solid fa-hand"></i>
                  <div>
                    <p>Sellers may <strong>not</strong> manipulate, delete, or bribe customers for ratings</p>
                  </div>
                </div>
              </div>
              <div class="term-note">
                <i class="fa-solid fa-trash-can"></i>
                <p>IPA may remove reviews that violate guidelines but does <strong>not guarantee deletion</strong></p>
              </div>
            </div>
          </div>

          <!-- Fees & Commission -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="800">
            <div class="term-number">9</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-percent"></i>
                Fees & Commission
              </h3>
              <span class="term-subtitle">Platform charges</span>
            </div>
            <div class="term-body">
              <div class="fees-info">
                <div class="fee-rule">
                  <i class="fa-solid fa-bullhorn"></i>
                  <div>
                    <p>If IPA charges listing fees or commission, fees will be <strong>communicated clearly</strong> before application</p>
                  </div>
                </div>
                <div class="fee-rule">
                  <i class="fa-solid fa-pen-to-square"></i>
                  <div>
                    <p>IPA may revise fees at any time with <strong>notice</strong></p>
                  </div>
                </div>
              </div>
              <div class="term-note">
                <i class="fa-solid fa-file-invoice-dollar"></i>
                <p>Check the Seller Dashboard for current fee structure</p>
              </div>
            </div>
          </div>

          <!-- Account Suspension & Termination -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="1000">
            <div class="term-number">10</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-user-slash"></i>
                Account Suspension & Termination
              </h3>
              <span class="term-subtitle">When we may close your account</span>
            </div>
            <div class="term-body">
              <p>IPA may suspend or terminate a Seller account if:</p>
              <div class="termination-reasons">
                <div class="reason">
                  <i class="fa-solid fa-user-secret"></i>
                  <span>Fraudulent or illegal activity is detected</span>
                </div>
                <div class="reason">
                  <i class="fa-solid fa-file-circle-xmark"></i>
                  <span>T&C violations occur</span>
                </div>
                <div class="reason">
                  <i class="fa-solid fa-people-arrows"></i>
                  <span>Multiple customer complaints are verified</span>
                </div>
                <div class="reason">
                  <i class="fa-solid fa-face-angry"></i>
                  <span>The Seller engages in abusive, harmful, or deceptive behavior</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Limitation of Liability -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="1100">
            <div class="term-number">11</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-scale-unbalanced"></i>
                Limitation of Liability
              </h3>
              <span class="term-subtitle">What IPA is not responsible for</span>
            </div>
            <div class="term-body">
              <p>IPA is <strong>not responsible</strong> for:</p>
              <div class="liability-grid">
                <div class="liability-item">
                  <i class="fa-solid fa-box-open"></i>
                  <span>Product quality, condition, authenticity, or safety</span>
                </div>
                <div class="liability-item">
                  <i class="fa-solid fa-truck"></i>
                  <span>Delivery issues or delays</span>
                </div>
                <div class="liability-item">
                  <i class="fa-solid fa-money-bill-transfer"></i>
                  <span>Payment disputes</span>
                </div>
                <div class="liability-item">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>Loss, theft, or damage arising from transactions</span>
                </div>
                <div class="liability-item">
                  <i class="fa-solid fa-comment-slash"></i>
                  <span>Miscommunication between buyers and sellers</span>
                </div>
              </div>
              <div class="term-warning important">
                <i class="fa-solid fa-user-tie"></i>
                <p>The Seller accepts <strong>full liability</strong> for products offered and delivered</p>
              </div>
            </div>
          </div>

          <!-- Indemnification -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="1200">
            <div class="term-number">12</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-handshake"></i>
                Indemnification
              </h3>
              <span class="term-subtitle">Your responsibility for claims</span>
            </div>
            <div class="term-body">
              <p>You agree to indemnify and hold IPA harmless from:</p>
              <div class="indemnity-list">
                <div class="indemnity-item">
                  <i class="fa-solid fa-scale-balanced"></i>
                  <div>
                    <strong>Product Claims:</strong> Claims regarding product defects, misrepresentation, or fraud
                  </div>
                </div>
                <div class="indemnity-item">
                  <i class="fa-solid fa-sack-dollar"></i>
                  <div>
                    <strong>Damages:</strong> Damages arising from your actions or negligence
                  </div>
                </div>
                <div class="indemnity-item">
                  <i class="fa-solid fa-gavel"></i>
                  <div>
                    <strong>Legal Issues:</strong> Legal violations related to your sales activity
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Data Protection -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="1300">
            <div class="term-number">13</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-database"></i>
                Data Protection
              </h3>
              <span class="term-subtitle">How we handle your information</span>
            </div>
            <div class="term-body">
              <div class="data-protection">
                <div class="data-rule">
                  <i class="fa-solid fa-lock"></i>
                  <div>
                    <p>IPA will store and process Seller data in accordance with its <router-link to="/privacy-policy" class="policy-link">Privacy Policy</router-link></p>
                  </div>
                </div>
                <div class="data-rule">
                  <i class="fa-solid fa-eye"></i>
                  <div>
                    <p>You agree that your name/company name, profile picture, rating, and location may be <strong>displayed to customers</strong></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Amendments -->
          <div class="term-card" data-aos="fade-up" data-aos-delay="1400">
            <div class="term-number">14</div>
            <div class="term-header">
              <h3>
                <i class="fa-solid fa-file-contract"></i>
                Amendments
              </h3>
              <span class="term-subtitle">Future changes to terms</span>
            </div>
            <div class="term-body">
              <div class="amendments-info">
                <div class="amendment-rule">
                  <i class="fa-solid fa-pen-to-square"></i>
                  <div>
                    <p>IPA may modify these Terms at any time</p>
                  </div>
                </div>
                <div class="amendment-rule">
                  <i class="fa-solid fa-check"></i>
                  <div>
                    <p>Continued use of the platform indicates <strong>acceptance of revised Terms</strong></p>
                  </div>
                </div>
                <div class="version-info">
                  <i class="fa-solid fa-clock-rotate-left"></i>
                  <span>Current Version: 1.0.0 | Effective: Apr 27, 2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary Section -->
        <div class="summary-section seller-summary">
          <h3 class="summary-title">
            <i class="fa-solid fa-clipboard-list"></i>
            Seller Responsibilities Summary
          </h3>
          <div class="seller-responsibilities">
            <div class="responsibility-item">
              <i class="fa-solid fa-circle-check"></i>
              <p>Provide accurate product descriptions and images</p>
            </div>
            <div class="responsibility-item">
              <i class="fa-solid fa-circle-check"></i>
              <p>Deliver products as promised and on time</p>
            </div>
            <div class="responsibility-item">
              <i class="fa-solid fa-circle-check"></i>
              <p>Communicate professionally with buyers</p>
            </div>
            <div class="responsibility-item">
              <i class="fa-solid fa-circle-check"></i>
              <p>Accept full liability for your products and transactions</p>
            </div>
            <div class="responsibility-item">
              <i class="fa-solid fa-circle-check"></i>
              <p>Comply with all laws and platform policies</p>
            </div>
          </div>
        </div>

        <!-- Footer Notice -->
        <div class="legal-footer-notice seller-terms-footer">
          <div class="footer-content">
            <div class="footer-right">
              <h4>Document Information</h4>
              <p><i class="fa-solid fa-file-contract"></i> IPA Seller Terms & Conditions</p>
              <p><i class="fa-solid fa-calendar"></i> Version 1.0.0 (April 27, 2026)</p>
              <p><i class="fa-solid fa-location-dot"></i> IPA Marketplace, Nairobi, Kenya</p>
            </div>
          </div>
          <div class="copyright">
            <p>Â© 2026 IPromiseAfrica Marketplace. All rights reserved.</p>
            <p class="legal-id">Legal Document ID: IPA-TC-2026-04-27</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="legal-actions seller-terms-actions">
          <div class="accept-section">
            <div class="accept-content">
              <i class="fa-solid fa-circle-exclamation"></i>
              <div>
                <p class="accept-title">By proceeding, you agree to these Terms & Conditions</p>
                <p class="accept-subtitle">This is a legally binding agreement</p>
              </div>
            </div>
            <div class="accept-buttons">
              <router-link to="/ipromiseafrica/resale/seller-sign-up" class="accept-btn">
                <i class="fa-solid fa-signature"></i>
                I Agree & Proceed to Seller Registration
              </router-link>
              <router-link to="/ipromiseafrica/legal/buyer-terms" class="view-other-btn">
                <i class="fa-solid fa-eye"></i>
                View Buyer Terms First
              </router-link>
              <router-link to="/ipromiseafrica/resale/buyer-product-view" class="back-btn">
                <i class="fa-solid fa-store"></i>
                Back to Marketplace
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";
import AOS from 'aos';
import 'aos/dist/aos.css';

export default {
  name: "SellerTerms",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  mounted() {
    // Initialize animations
    AOS.init({
      duration: 600,
      once: true,
      offset: 100
    });
    
    // Scroll to top
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped src="./SellerTerms.styles.css"></style>
